import React, { useState } from 'react';
import EditProductModal from './EditProductModal';
import ManageDealerBillsModal from './ManageDealerBillsModal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Edit, Trash2, Check, X, Package, FileSpreadsheet, Plus, Minus } from 'lucide-react';
import { cn } from '../lib/utils';

const ProductCard = ({ product, onUpdateStock, onDelete, onEdit, onError }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newStock, setNewStock] = useState(product.stockQty || product.stock || 0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManageBillsModal, setShowManageBillsModal] = useState(false);
  const [showSkuDialog, setShowSkuDialog] = useState(false);
  const [skuInput, setSkuInput] = useState('');
  const [skuAdjustmentType, setSkuAdjustmentType] = useState('add'); // 'add' or 'remove'

  const productId = product.productId || product._id;

  const handleStockUpdate = () => {
    if (newStock >= 0) {
      onUpdateStock(productId, parseInt(newStock), currentStock);
      setIsEditing(false);
    }
  };

  const handleQuickAdjust = (amount) => {
    if (product.productType === 'SERIALIZED') {
      setSkuAdjustmentType(amount > 0 ? 'add' : 'remove');
      setShowSkuDialog(true);
    } else {
      const updatedStock = Math.max(0, currentStock + amount);
      onUpdateStock(productId, updatedStock, currentStock);
    }
  };

  const handleSkuSubmit = () => {
    const currentSkus = Array.from(product.skuProductCodes || []);
    if (skuAdjustmentType === 'add') {
      if (!skuInput.trim()) return;
      const updatedSkus = [...currentSkus, skuInput.trim()];
      onUpdateStock(productId, currentStock + 1, currentStock, updatedSkus);
    } else {
      // Remove the specific SKU or last one if input empty
      let updatedSkus;
      if (skuInput.trim()) {
        updatedSkus = currentSkus.filter(s => s !== skuInput.trim());
      } else {
        updatedSkus = currentSkus.slice(0, -1);
      }
      onUpdateStock(productId, currentStock - 1, currentStock, updatedSkus);
    }
    setShowSkuDialog(false);
    setSkuInput('');
  };

  const displayName = product.productName || product.name || (product.brand && product.model ? `${product.brand} ${product.model}` : 'Unknown Product');
  const price = product.purchasePriceInr || product.price || 0;
  const currentStock = product.stockQty || product.stock || 0;
  const description = product.description || product.specification;

  return (
    <>
      <Card className="group relative overflow-hidden bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-xl shadow-black/5 border border-slate-100 dark:border-slate-700 transition-all hover:-translate-y-2 hover:shadow-2xl">
        {/* Visual Accent */}
        <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none opacity-50 transition-opacity group-hover:opacity-100"></div>
        
        <div className="relative flex justify-between items-start gap-4 mb-4">
          <div className="space-y-1 min-w-0 flex-1">
            <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight leading-tight line-clamp-2 break-words" title={displayName}>
              {displayName}
            </h3>
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 truncate">
              <Package className="h-3 w-3 flex-shrink-0" />
              <span className="truncate whitespace-nowrap">Ref: {productId.toString().substring(0, 8)}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0 pt-1">
             <div className="text-xl font-black text-emerald-600 tabular-nums whitespace-nowrap bg-emerald-50/50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-lg border border-emerald-100/50 dark:border-emerald-800/50 shadow-sm">
                ₹{price.toLocaleString('en-IN')}
             </div>
          </div>
        </div>

        <div className="relative space-y-4">
          {description && (
            <p className="text-xs font-medium text-slate-500 line-clamp-2 leading-relaxed h-8">
              {description}
            </p>
          )}

          <div className="flex items-center gap-2">
            <span className={cn(
                "text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-md border",
                product.productType === 'SERIALIZED' 
                    ? "bg-indigo-50 text-indigo-600 border-indigo-100" 
                    : "bg-emerald-50 text-emerald-600 border-emerald-100"
            )}>
              {product.productType === 'SERIALIZED' ? 'SKU-Tracked' : 'NON-SKU (Bulk)'}
            </span>
            {product.productType === 'SERVICE' && (
                <span className="text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-md border bg-amber-50 text-amber-600 border-amber-100">Service</span>
            )}
          </div>

          {(product.processor || product.ram || product.storage) && (
            <div className="flex flex-wrap gap-2">
              {[product.processor, product.ram, product.storage].filter(Boolean).map((spec, i) => (
                <span key={i} className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md border border-slate-200/50">
                  {spec}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 p-3 rounded-2xl bg-slate-50/80 border border-slate-100 min-w-0">
            <div className="flex flex-col min-w-0">
              <span className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">Current Stock</span>
              {isEditing ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    value={newStock}
                    onChange={(e) => setNewStock(e.target.value)}
                    className="h-8 w-16 text-xs px-2 rounded-lg font-black"
                    min="0"
                  />
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={handleStockUpdate} className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white shadow-sm">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setIsEditing(false)} className="h-8 w-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white shadow-sm">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 group/stock mt-0.5">
                  <div 
                    className={cn(
                        "flex items-center gap-2",
                        product.productType === 'SERIALIZED' ? "cursor-default" : "cursor-pointer"
                    )}
                    onClick={() => product.productType !== 'SERIALIZED' && setIsEditing(true)}
                  >
                    <span className={cn(
                      "text-xl font-black tabular-nums",
                      currentStock === 0 ? "text-rose-600" : "text-slate-800"
                    )}>
                      {currentStock}
                    </span>
                    {product.productType !== 'SERIALIZED' && (
                        <div className="opacity-40 hover:opacity-100 transition-opacity bg-slate-200 text-slate-700 p-1 rounded-lg">
                          <Edit className="h-3 w-3" />
                        </div>
                    )}
                  </div>
                  
                  {/* Quick Adjust Buttons */}
                  <div className="flex items-center gap-1.5 ml-2 transition-all">
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      onClick={() => handleQuickAdjust(1)}
                      className="h-8 w-8 rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      onClick={() => handleQuickAdjust(-1)}
                      className="h-8 w-8 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {currentStock === 0 && (
                <Badge className="bg-rose-100 text-rose-600 border-0 font-black text-[9px] uppercase tracking-widest px-2 shadow-sm shadow-rose-500/10 whitespace-nowrap flex-shrink-0">Out of Stock</Badge>
            )}
          </div>

          <div className="grid grid-cols-4 gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowManageBillsModal(true)}
              className="h-11 rounded-xl border-slate-200 bg-white shadow-sm hover:bg-indigo-600 hover:text-white hover:border-indigo-600 active:scale-95 transition-all col-span-2 group/bill"
              title="Manage Dealer Bills"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4 text-indigo-600 group-hover/bill:text-white" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 group-hover/bill:text-white">Dealer Bills</span>
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowEditModal(true)}
              className="h-11 rounded-xl border-slate-200 bg-white shadow-sm hover:bg-slate-900 hover:text-white active:scale-95 transition-all"
            >
              <Edit className="h-4 w-4 text-slate-700 hover:text-white" />
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this product?')) {
                  onDelete(productId);
                }
              }}
              className="h-11 rounded-xl border-rose-100 bg-rose-50 text-rose-600 shadow-sm hover:bg-rose-600 hover:text-white active:scale-95 transition-all"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {showEditModal && (
        <EditProductModal
          product={product}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedProduct) => {
            if (onEdit) {
              onEdit(productId, updatedProduct);
            }
            // Handled by EditProductModal's internal countdown logic
          }}
        />
      )}

      {showManageBillsModal && (
        <ManageDealerBillsModal
          productId={productId}
          isOpen={showManageBillsModal}
          onClose={() => setShowManageBillsModal(false)}
        />
      )}

      {/* SKU Adjustment Dialog */}
      <Dialog open={showSkuDialog} onOpenChange={setShowSkuDialog}>
        <DialogContent className="max-w-md p-6 rounded-[2rem] border-0 shadow-2xl bg-white/95 backdrop-blur-xl animate-in zoom-in-95 duration-300 overflow-visible">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tight text-slate-800 flex items-center gap-2">
              <Package className={cn("h-6 w-6", skuAdjustmentType === 'add' ? "text-emerald-500" : "text-rose-500")} />
              {skuAdjustmentType === 'add' ? "Assign New Identity" : "Decommission Unit"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest px-1">
              {skuAdjustmentType === 'add' ? "Enter unique SKU/Serial code for the new inventory unit" : "Select or enter the SKU code of the unit being decommissioned"}
            </p>
            
            <div className="relative group">
               <Input
                  value={skuInput}
                  onChange={(e) => setSkuInput(e.target.value)}
                  placeholder={skuAdjustmentType === 'add' ? "e.g., SN-2024-XP-001" : "e.g., SN-2024-XP-001 or leave blank for last"}
                  className="h-14 bg-slate-50 border-slate-100 rounded-2xl px-5 font-black text-slate-800 placeholder:text-slate-300 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-inner"
               />
               <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none opacity-20 group-hover:opacity-100 transition-opacity">
                  <Check className="h-5 w-5 text-emerald-500" />
               </div>
            </div>

            {skuAdjustmentType === 'remove' && (product.skuProductCodes || []).length > 0 && (
                <div className="space-y-2 mt-4">
                  <span className="text-[10px] font-black uppercase text-slate-400">Current Ledger Registry</span>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 bg-slate-50 rounded-2xl border border-slate-100 shadow-inner custom-scrollbar">
                    {Array.from(product.skuProductCodes).map((sku, i) => (
                      <button
                        key={i}
                        onClick={() => setSkuInput(sku)}
                        className={cn(
                          "px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border transition-all",
                          skuInput === sku 
                            ? "bg-rose-500 text-white border-rose-500 shadow-lg shadow-rose-500/20 animate-in zoom-in-90" 
                            : "bg-white text-slate-600 border-slate-100 hover:border-slate-300 hover:bg-slate-50"
                        )}
                      >
                        {sku}
                      </button>
                    ))}
                  </div>
                </div>
            )}
          </div>

          <DialogFooter className="gap-3 sm:gap-2">
            <Button 
                variant="ghost" 
                onClick={() => setShowSkuDialog(false)}
                className="h-14 rounded-2xl font-black uppercase tracking-widest text-slate-400 hover:bg-slate-50 flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button 
                onClick={handleSkuSubmit}
                className={cn(
                    "h-14 rounded-2xl font-black uppercase tracking-widest text-white flex-1 sm:flex-none px-8 shadow-xl shadow-emerald-500/20 active:scale-95 transition-all",
                    skuAdjustmentType === 'add' ? "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20" : "bg-rose-500 hover:bg-rose-600 shadow-rose-500/20"
                )}
            >
              Confirm Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductCard;
